import { useState, useEffect, useCallback } from 'react';
import api from '../../lib/api';
import { toast } from 'sonner';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Loader2 } from 'lucide-react';
import { statusLabel, levelLabel, levelBadgeClass } from './conductUtils';

export default function MyReviewsTab() {
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [day, setDay] = useState({});
  const [overall, setOverall] = useState({});
  const [note, setNote] = useState({});
  const [capMet, setCapMet] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/conduct/reviews/mine');
      setCards(res.data);
    } catch (e) { toast.error('Failed to load your reviews'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const submitReview = async (card) => {
    const d = Number(day[card.id]);
    if (!d || !overall[card.id]) { toast.error('Pick a review day and an outcome'); return; }
    const isLast = d === Math.max(...(card.cap?.review_days || [0]));
    if (isLast && !capMet[card.id]) { toast.error("Pick whether the CAP was met on this final review day"); return; }
    try {
      await api.post(`/conduct/cards/${card.id}/reviews`, {
        day: d, overall: overall[card.id], note: note[card.id] || '', action_statuses: [],
        ...(isLast ? { cap_met: capMet[card.id] === 'true' } : {}),
      });
      toast.success('Review recorded');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to record review'); }
  };

  if (loading) return <div className="flex justify-center py-12" data-testid="my-reviews-loading"><Loader2 className="animate-spin" /></div>;

  return (
    <div className="space-y-4" data-testid="my-reviews-tab">
      {cards.length === 0 && <p className="text-sm text-muted-foreground" data-testid="my-reviews-empty">No CAPs assigned to you for review.</p>}
      {cards.map((card) => (
        <Card key={card.id} data-testid={`review-card-${card.id}`}>
          <CardContent className="pt-4 space-y-3">
            <div className="flex items-center justify-between">
              <Badge className={levelBadgeClass(card.level)}>{levelLabel(card.level)}</Badge>
              <Badge variant="outline">{statusLabel(card.status, card, 'hr')}</Badge>
            </div>
            <ul className="text-sm list-disc pl-4">
              {card.cap?.actions?.map((a) => <li key={a.id}>{a.text} (target: {a.target}, due day {a.due_review_day})</li>)}
            </ul>
            {card.status === 'ACTIVE_CAP' && (
              <div className="flex flex-wrap items-end gap-2">
                <Select value={day[card.id] || ''} onValueChange={(v) => setDay({ ...day, [card.id]: v })}>
                  <SelectTrigger className="w-28" data-testid={`review-day-select-${card.id}`}><SelectValue placeholder="Day" /></SelectTrigger>
                  <SelectContent>{(card.cap?.review_days || []).map((d) => <SelectItem key={d} value={String(d)}>{d}</SelectItem>)}</SelectContent>
                </Select>
                <Select value={overall[card.id] || ''} onValueChange={(v) => setOverall({ ...overall, [card.id]: v })}>
                  <SelectTrigger className="w-32" data-testid={`review-outcome-select-${card.id}`}><SelectValue placeholder="Outcome" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="on_track">On Track</SelectItem>
                    <SelectItem value="at_risk">At Risk</SelectItem>
                    <SelectItem value="failed">Failed</SelectItem>
                  </SelectContent>
                </Select>
                {Number(day[card.id]) === Math.max(...(card.cap?.review_days || [0])) && (
                  <Select value={capMet[card.id] || ''} onValueChange={(v) => setCapMet({ ...capMet, [card.id]: v })}>
                    <SelectTrigger className="w-32" data-testid={`review-cap-met-select-${card.id}`}><SelectValue placeholder="CAP met?" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="true">CAP Met</SelectItem>
                      <SelectItem value="false">Not Met</SelectItem>
                    </SelectContent>
                  </Select>
                )}
                <Textarea
                  className="w-56 h-8 text-xs"
                  placeholder="Note"
                  data-testid={`review-note-input-${card.id}`}
                  value={note[card.id] || ''}
                  onChange={(e) => setNote({ ...note, [card.id]: e.target.value })}
                />
                <Button size="sm" data-testid={`submit-review-btn-${card.id}`} onClick={() => submitReview(card)}>Record Review</Button>
              </div>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
